import { message } from 'antd';
import type { TFunction } from 'i18next';

/** How long to keep the blob URL alive before revoking. 60 s is long enough
 *  for any browser viewer to have parsed and buffered the bytes, short
 *  enough that previewing many files doesn't stack up MB of live blob URLs.
 *  If a slow renderer edge case shows up we can bump this — the tab already
 *  has a copy of the parsed content by the time the URL is gone. */
const PREVIEW_URL_TTL_MS = 60_000;
/** Half a second is enough for the browser's download machinery to read the
 *  blob URL into its own stream. Revoking earlier can truncate the download
 *  on Safari. */
const DOWNLOAD_URL_TTL_MS = 500;

/**
 * Extensions whose bytes a browser only renders inline if the BLOB says so, and the type to
 * force for each.
 *
 * <p>
 * `File.type` is filled in by the OS from its own extension→MIME table, and for text formats it
 * is routinely EMPTY — Windows reports nothing for `.csv` on a machine with no spreadsheet app
 * registered, and a file dragged out of an archive or a cloud client can arrive as
 * `application/octet-stream`. A blob URL carrying either of those makes the browser DOWNLOAD
 * instead of render: the advisor clicks Preview, a file quietly lands in their Downloads folder,
 * and nothing opens. That is indistinguishable from a broken button, which is the same failure
 * {@link openFileInNewTab}'s popup-blocked warning exists to avoid.
 * </p>
 *
 * <p>
 * Forcing the type closes the other direction too. A `.txt` that arrived typed `text/html` —
 * which a drag from some sources will do — would otherwise be rendered as a DOCUMENT on a blob
 * URL, and a blob URL inherits this app's origin. Pinning `text/plain` means the bytes are shown,
 * never executed.
 * </p>
 *
 * <p>
 * `csv` is deliberately `text/plain` rather than `text/csv`: the point here is to show the
 * advisor what they just uploaded, and every browser renders `text/plain` inline while `text/csv`
 * is a download in most of them.
 * </p>
 *
 * <p>
 * The charset is pinned to UTF-8, so a file saved as Windows-1252 shows mojibake on its accented
 * characters. Accepted: sniffing the encoding client-side is a disproportionate amount of
 * machinery for a preview, and the delivered bytes are untouched either way — only this throwaway
 * view is affected.
 * </p>
 */
const FORCED_PREVIEW_TYPE: Readonly<Record<string, string>> = {
  txt: 'text/plain;charset=utf-8',
  csv: 'text/plain;charset=utf-8',
};

/**
 * The blob to hand a preview tab for a picked file — the file itself for everything the OS types
 * usefully, or a retyped copy for the formats in {@link FORCED_PREVIEW_TYPE}.
 *
 * <p>
 * Separate from the step's own previewable allow-list on purpose: that list answers "does this
 * file get a Preview button at all?", which is a product decision about what is worth showing.
 * This answers "what do we hand the tab once it does?", which is browser mechanics. Keeping them
 * apart is what lets both preview call sites share this without sharing the allow-list.
 * </p>
 */
export function previewBlobFor(file: File): Blob {
  const dot = file.name.lastIndexOf('.');
  const extension = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : '';
  const forced = FORCED_PREVIEW_TYPE[extension];
  // `new Blob([file])` re-wraps without copying the bytes — a File IS a Blob, so this is a
  // retyped view of the same data, not a second megabyte in memory.
  return forced ? new Blob([file], { type: forced }) : file;
}

/**
 * Open a Blob in a new tab via a blob URL, then revoke after a TTL
 * so the URL doesn't leak into long-lived tabs. Falls back to a
 * `message.warning` when the popup was blocked so the advisor knows the
 * click did fire — a silent no-op looks like a broken button.
 *
 * Shared by `AdditionalDocumentsStep` (per-file Preview button),
 * `PdfGenerationStep` (per-uploaded-file Preview button), and the originator's
 * `PdfEntriesDrawer` previews. Same behavior everywhere so a click on the same
 * file feels identical across the flow.
 *
 * Takes `Blob` rather than `File` because the drawer previews fetch stored bytes back
 * through the API (ADR-0028) and never hold the original `File`. Every `File` is a
 * `Blob`, so the picked-file callers are unaffected.
 *
 * `popupBlockedKey` is the i18n key to feed `message.warning` when the
 * popup was blocked. Callers pass their own so the copy matches the
 * step's other translated strings.
 */
export function openFileInNewTab(
  file: Blob,
  t: TFunction,
  popupBlockedKey = 'formRenderer.additionalDocuments.popupBlocked',
): void {
  const url = URL.createObjectURL(file);
  // Deliberately NOT `window.open(url, '_blank', 'noopener,noreferrer')`. Per the HTML spec, a
  // `noopener` feature makes `window.open` return null ALWAYS — there is no opener relationship
  // left to hand back a WindowProxy through — and `noreferrer` implies `noopener`. Every modern
  // browser implements that, so the null return stopped meaning "blocked" and started meaning
  // "opened fine", and the warning below fired on every successful preview while the tab was
  // visibly opening next to it. Keeping the feature out is what makes the null return load-bearing
  // again: it now means the pop-up was genuinely blocked, and nothing else.
  const win = window.open(url, '_blank');
  if (!win) {
    URL.revokeObjectURL(url);
    void message.warning(t(popupBlockedKey));
    return;
  }
  // Sever the opener by hand instead — the pre-`noopener` idiom, and the only one compatible with
  // detecting a blocked pop-up. The preview tab is a blob URL, which inherits this app's origin,
  // so the assignment is same-origin and takes effect. Best-effort: a browser that refuses the
  // write must not cost the advisor their preview, and the tab is our own bytes either way.
  try {
    win.opener = null;
  } catch {
    /* empty */
  }
  window.setTimeout(() => URL.revokeObjectURL(url), PREVIEW_URL_TTL_MS);
}

/**
 * Trigger a browser download of a picked File under its original filename.
 * Uses the anchor-with-download-attr pattern — the browser handles the
 * "Save As" dialog. Revokes the blob URL after a short delay so the download
 * completes but no long-lived URL leaks.
 */
export function downloadFileToDevice(file: File): void {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_TTL_MS);
}
